/**
 * SalesDepartmentAnalysis Component
 *
 * Forecast-page card mirroring the ServiceGrossBreakdown card's structure.
 * Displays MTD Good Leads (sourced from VinSolutions CloudStore) with a
 * by-source-group pie, a location closing-ratio stat, and an expandable
 * per-salesperson Closing Ratio drill-down (joins VinSolutions touched-
 * attribution with DealerBuilt units sold).
 */

import React, { useMemo, useState } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { MegaphoneIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { SalesLeadsPacingResponse, SalesAppointmentsResponse } from '../../services/api';
import type { ClosingRatioRow } from '../../hooks/useSalesLeadsData';
import { LeadPacingTile } from './LeadPacingTile';
import { SalesFunnelTable } from './SalesFunnelTable';

interface SalesDepartmentAnalysisProps {
  locationId: number;
  leadsData: SalesLeadsPacingResponse | null;
  loading: boolean;
  error: string | null;
  year: number;
  month: number;
  closingRatioRows: ClosingRatioRow[];
  unmatchedCount: number;
  appointmentsData: SalesAppointmentsResponse | null;
  appointmentsLoading: boolean;
  appointmentsError: string | null;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

type ClosingSortField = 'name' | 'leads' | 'units' | 'ratio';
type ClosingSortDir = 'asc' | 'desc';

// Source-group pie palette — distinct from Service card's pay-type palette.
const SOURCE_COLORS = [
  '#3b82f6', // blue-500
  '#8b5cf6', // violet-500
  '#10b981', // emerald-500
  '#f59e0b', // amber-500
  '#ec4899', // pink-500
  '#06b6d4', // cyan-500
  '#9ca3af', // gray-400 (Other bucket)
];
const TOP_SOURCE_COUNT = 6;

function formatNumber(n: number): string {
  return new Intl.NumberFormat('en-US').format(n);
}

function getMonthName(month: number): string {
  return [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ][month - 1] || '';
}

const SourceTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 max-w-xs">
        <p className="font-semibold text-gray-900 mb-1">{data.label}</p>
        <p className="text-sm text-gray-600">
          Good Leads:{' '}
          <span className="font-medium text-blue-600">{formatNumber(data.value)}</span>
        </p>
        <p className="text-sm text-gray-600">
          Share: <span className="font-medium">{data.percentage.toFixed(1)}%</span>
        </p>
      </div>
    );
  }
  return null;
};

const SourceLegend = (props: any) => {
  const { payload } = props;
  if (!payload) return null;

  return (
    <div className="flex flex-col justify-center gap-2 pl-2">
      {payload.map((entry: any, index: number) => {
        const data = entry.payload;
        return (
          <div key={index} className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-sm flex-shrink-0"
              style={{ backgroundColor: entry.color }}
            />
            <div className="text-sm">
              <span className="text-gray-700">{data.label}</span>
              <span className="text-gray-500 ml-1">{formatNumber(data.value)}</span>
              <span className="text-gray-400 ml-1">({data.percentage.toFixed(0)}%)</span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

function shortReason(reason: string): string {
  if (reason === 'Sales Appointment') return 'Sales';
  return reason;
}

function formatLastUpdated(isoString?: string): string {
  if (!isoString) return '';
  const d = new Date(isoString);
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: 'America/New_York',
  });
}

export const SalesDepartmentAnalysis: React.FC<SalesDepartmentAnalysisProps> = ({
  locationId,
  leadsData,
  loading,
  error,
  year,
  month,
  closingRatioRows,
  unmatchedCount,
  appointmentsData,
  appointmentsLoading,
  appointmentsError,
  collapsed = false,
  onToggleCollapse,
}) => {
  const [closingExpanded, setClosingExpanded] = useState(false);
  const [apptExpanded, setApptExpanded] = useState(false);
  const [apptSpExpanded, setApptSpExpanded] = useState<Set<number>>(new Set());
  const toggleApptSp = (userId: number) => {
    setApptSpExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };
  const [closingSort, setClosingSort] = useState<{ field: ClosingSortField; dir: ClosingSortDir }>(
    { field: 'units', dir: 'desc' },
  );

  // Top source groups + bucket the remainder into "Other".
  const sourceChartData = useMemo(() => {
    if (!leadsData || !leadsData.bySourceGroup) return [];
    const entries = Object.entries(leadsData.bySourceGroup)
      .filter(([, count]) => count > 0)
      .sort((a, b) => b[1] - a[1]);

    const total = entries.reduce((sum, [, c]) => sum + c, 0);
    if (total === 0) return [];

    const top = entries.slice(0, TOP_SOURCE_COUNT);
    const rest = entries.slice(TOP_SOURCE_COUNT);
    const restTotal = rest.reduce((sum, [, c]) => sum + c, 0);

    const series = top.map(([label, count], i) => ({
      label,
      value: count,
      percentage: (count / total) * 100,
      color: SOURCE_COLORS[i] ?? '#9ca3af',
    }));

    if (restTotal > 0) {
      series.push({
        label: `Other (${rest.length})`,
        value: restTotal,
        percentage: (restTotal / total) * 100,
        color: SOURCE_COLORS[SOURCE_COLORS.length - 1],
      });
    }
    return series;
  }, [leadsData]);

  const sortedClosingRows = useMemo(() => {
    const rows = [...closingRatioRows];
    const dir = closingSort.dir === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      let cmp = 0;
      switch (closingSort.field) {
        case 'name':
          cmp = a.name.localeCompare(b.name);
          break;
        case 'leads':
          cmp = a.leads - b.leads;
          break;
        case 'units':
          cmp = a.units - b.units;
          break;
        case 'ratio': {
          // Treat null ratio as -Infinity for desc, +Infinity for asc so unmatched rows
          // never break up the meaningful section of the table.
          const av = a.ratioPct ?? (dir === 1 ? Infinity : -Infinity);
          const bv = b.ratioPct ?? (dir === 1 ? Infinity : -Infinity);
          cmp = av - bv;
          break;
        }
      }
      return cmp * dir;
    });
    return rows;
  }, [closingRatioRows, closingSort]);

  // Totals row: column sums + closing % = sum(units) / sum(leads). One-decimal
  // rounding mirrors the per-row formula in useSalesLeadsData (units/leads × 1000 ÷ 10).
  const closingTotals = useMemo(() => {
    const leads = closingRatioRows.reduce((s, r) => s + r.leads, 0);
    const units = closingRatioRows.reduce((s, r) => s + r.units, 0);
    const ratioPct = leads > 0 ? Math.round((units / leads) * 1000) / 10 : null;
    return { leads, units, ratioPct };
  }, [closingRatioRows]);

  function toggleSort(field: ClosingSortField) {
    setClosingSort((prev) =>
      prev.field === field
        ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { field, dir: field === 'name' ? 'asc' : 'desc' },
    );
  }

  function sortIndicator(field: ClosingSortField): string {
    if (closingSort.field !== field) return '';
    return closingSort.dir === 'asc' ? ' ▲' : ' ▼';
  }

  const dataAsOf = leadsData?.dataAsOfDate;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header */}
      <div
        className={`px-4 py-3 ${collapsed ? '' : 'border-b border-gray-200'} ${onToggleCollapse ? 'cursor-pointer select-none hover:bg-gray-50 transition-colors' : ''}`}
        onClick={onToggleCollapse}
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <MegaphoneIcon className="h-5 w-5 text-blue-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900">
              Sales Department Analysis
            </h3>
            <p className="text-sm text-gray-500">
              {getMonthName(month)} {year}
              {dataAsOf && (
                <span className="ml-2 text-xs text-gray-400">· data through {dataAsOf}</span>
              )}
            </p>
          </div>
          {onToggleCollapse && (
            <ChevronDownIcon
              className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${collapsed ? '' : 'rotate-180'}`}
            />
          )}
        </div>
      </div>

      {/* Content */}
      {!collapsed && (
        <div className="p-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 mb-4 text-sm">
              {error}
            </div>
          )}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left column: pacing tile (replaces the old big-number Good Leads MTD) */}
            <div className="flex flex-col">
              <LeadPacingTile leadsData={leadsData} loading={loading} monthName={getMonthName(month)} />
            </div>

            {/* Right column: by Lead Source Group */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">By Lead Source Group</h4>
              {sourceChartData.length === 0 ? (
                <div className="h-64 flex items-center justify-center text-gray-500 text-sm">
                  {loading ? 'Loading…' : 'No source data available'}
                </div>
              ) : (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={sourceChartData}
                        cx="40%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={2}
                        dataKey="value"
                        nameKey="label"
                      >
                        {sourceChartData.map((entry, index) => (
                          <Cell key={`src-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip content={<SourceTooltip />} />
                      <Legend
                        layout="vertical"
                        align="right"
                        verticalAlign="middle"
                        content={SourceLegend}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </div>

          {/* Sales Funnel: per-salesperson past/next 5-working-day leading indicators */}
          <SalesFunnelTable locationId={locationId} />

          {/* Closing Ratio drill-down: per-salesperson leads (VinSolutions) vs units (DealerBuilt) */}
          <div className="mt-4 border border-gray-200 rounded-lg">
            <button
              type="button"
              onClick={() => setClosingExpanded((v) => !v)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <h4 className="text-sm font-medium text-gray-700">Closing Ratio</h4>
                <span className="text-xs text-gray-500">
                  {closingRatioRows.length} salespeople
                  {unmatchedCount > 0 && (
                    <span className="ml-2 text-amber-600">
                      · {unmatchedCount} with leads-only or units-only
                    </span>
                  )}
                </span>
              </div>
              <ChevronDownIcon
                className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${closingExpanded ? 'rotate-180' : ''}`}
              />
            </button>
            {closingExpanded && (
              <div className="border-t border-gray-200 overflow-x-auto">
                {sortedClosingRows.length === 0 ? (
                  <div className="p-6 text-center text-sm text-gray-500">
                    {loading ? 'Loading…' : 'No salesperson data available yet.'}
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                      <tr>
                        <th
                          className="px-4 py-2 text-left cursor-pointer select-none hover:text-gray-700"
                          onClick={() => toggleSort('name')}
                        >
                          Salesperson{sortIndicator('name')}
                        </th>
                        <th
                          className="px-4 py-2 text-right cursor-pointer select-none hover:text-gray-700"
                          onClick={() => toggleSort('leads')}
                        >
                          Leads MTD{sortIndicator('leads')}
                        </th>
                        <th
                          className="px-4 py-2 text-right cursor-pointer select-none hover:text-gray-700"
                          onClick={() => toggleSort('units')}
                        >
                          Units Sold (N+U){sortIndicator('units')}
                        </th>
                        <th
                          className="px-4 py-2 text-right cursor-pointer select-none hover:text-gray-700"
                          onClick={() => toggleSort('ratio')}
                        >
                          Closing %{sortIndicator('ratio')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedClosingRows.map((row) => (
                        <tr
                          key={row.name}
                          className={`border-t border-gray-100 ${row.unmatched ? 'bg-amber-50/40' : ''}`}
                        >
                          <td className="px-4 py-2 text-gray-900">{row.name}</td>
                          <td className="px-4 py-2 text-right text-gray-900">
                            {row.leads > 0 ? formatNumber(row.leads) : <span className="text-gray-400">—</span>}
                          </td>
                          <td className="px-4 py-2 text-right text-gray-900">
                            {row.units > 0 ? formatNumber(row.units) : <span className="text-gray-400">—</span>}
                          </td>
                          <td className="px-4 py-2 text-right font-medium text-gray-900">
                            {row.ratioPct !== null ? `${row.ratioPct.toFixed(1)}%` : <span className="text-gray-400">—</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-gray-300 bg-gray-50 font-semibold text-gray-900">
                        <td className="px-4 py-2 text-left">Total</td>
                        <td className="px-4 py-2 text-right">{formatNumber(closingTotals.leads)}</td>
                        <td className="px-4 py-2 text-right">{formatNumber(closingTotals.units)}</td>
                        <td className="px-4 py-2 text-right">
                          {closingTotals.ratioPct !== null ? `${closingTotals.ratioPct.toFixed(1)}%` : '—'}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                )}
                <div className="px-4 py-2 text-xs text-gray-500 bg-gray-50 border-t border-gray-200">
                  Leads MTD: VinSolutions task + showroomvisit attribution (assigned or touched).
                  Units: DealerBuilt salesperson-performance, New + Used, swap and wholesale excluded.
                </div>
              </div>
            )}
          </div>

          {/* Sales Appointments — 5-day forward outlook from VinSolutions */}
          <div className="mt-4 border border-gray-200 rounded-lg">
            <button
              type="button"
              onClick={() => setApptExpanded((v) => !v)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <h4 className="text-sm font-medium text-gray-700">Sales Appointments — 5-day outlook</h4>
                <span className="text-xs text-gray-500">
                  {appointmentsLoading
                    ? 'Loading…'
                    : `${appointmentsData?.total ?? 0} scheduled (today + next 4 days)`}
                </span>
              </div>
              <ChevronDownIcon
                className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${apptExpanded ? 'rotate-180' : ''}`}
              />
            </button>
            {apptExpanded && (
              <div className="border-t border-gray-200">
                {appointmentsError && (
                  <div className="bg-red-50 border-b border-red-200 text-red-700 px-4 py-2 text-sm">
                    {appointmentsError}
                  </div>
                )}
                {(() => {
                  const days = appointmentsData?.days || [];
                  const total = appointmentsData?.total ?? 0;
                  const salesPeople = appointmentsData?.salesPeople || [];
                  const colCount = days.length + 2;

                  if (appointmentsLoading) {
                    return <div className="p-6 text-center text-sm text-gray-500">Loading…</div>;
                  }
                  if (days.length === 0 || (total === 0 && salesPeople.length === 0)) {
                    return (
                      <div className="p-6 text-center text-sm text-gray-500">
                        No appointments scheduled in this window.
                      </div>
                    );
                  }
                  return (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs border-collapse">
                        <thead>
                          <tr className="bg-purple-50">
                            <th className="text-left px-2 py-1.5 font-semibold text-purple-800 border-b border-purple-200">
                              Salesperson
                            </th>
                            {days.map((day, idx) => (
                              <th
                                key={day.date}
                                className={`text-right px-2 py-1.5 font-semibold border-b border-purple-200 whitespace-nowrap ${idx === 0 ? 'text-purple-900' : 'text-purple-700'}`}
                              >
                                {day.dayLabel}
                              </th>
                            ))}
                            <th className="text-right px-2 py-1.5 font-semibold text-purple-900 border-b border-purple-200">
                              Total
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {/* Location aggregate row */}
                          <tr className="bg-purple-50/50 border-t border-purple-200">
                            <td className="px-2 py-1 font-bold text-gray-900 whitespace-nowrap">
                              {appointmentsData?.locationName || 'Location total'}
                            </td>
                            {days.map((day, idx) => (
                              <td
                                key={day.date}
                                className={`px-2 py-1 text-right tabular-nums font-bold ${idx === 0 ? 'text-purple-800' : 'text-gray-900'}`}
                              >
                                {day.scheduled}
                              </td>
                            ))}
                            <td className="px-2 py-1 text-right tabular-nums font-bold text-purple-900">
                              {total}
                            </td>
                          </tr>
                          {/* Per-salesperson rows */}
                          {salesPeople.map((sp) => {
                            const hasDetails = sp.details && sp.details.some((d) => d.length > 0);
                            const isSpExpanded = apptSpExpanded.has(sp.userId);
                            return (
                              <React.Fragment key={sp.userId}>
                                <tr
                                  className={`border-b border-purple-50 ${hasDetails ? 'cursor-pointer hover:bg-purple-50 select-none' : 'hover:bg-purple-50'}`}
                                  onClick={hasDetails ? () => toggleApptSp(sp.userId) : undefined}
                                >
                                  <td className="px-2 py-0.5 pl-5 text-gray-600 whitespace-nowrap">
                                    {hasDetails && (
                                      <span className="inline-block w-3 text-purple-400">
                                        {isSpExpanded ? '▾' : '▸'}
                                      </span>
                                    )}
                                    {sp.userName}
                                  </td>
                                  {sp.days.map((count, idx) => (
                                    <td
                                      key={idx}
                                      className={`px-2 py-0.5 text-right tabular-nums ${idx === 0 ? 'text-purple-700' : 'text-gray-500'}`}
                                    >
                                      {count > 0 ? count : ''}
                                    </td>
                                  ))}
                                  <td className="px-2 py-0.5 text-right tabular-nums text-gray-600">
                                    {sp.total}
                                  </td>
                                </tr>
                                {hasDetails && isSpExpanded && (
                                  <tr>
                                    <td colSpan={colCount} className="px-2 py-1 pl-8 bg-purple-50/30">
                                      <div
                                        className="grid"
                                        style={{ gridTemplateColumns: `repeat(${days.length}, 1fr)`, gap: '4px' }}
                                      >
                                        {days.map((day, idx) => {
                                          const dayDetails = sp.details?.[idx] || [];
                                          return (
                                            <div key={day.date} className="min-w-0">
                                              <div
                                                className="text-purple-600 font-semibold mb-0.5"
                                                style={{ fontSize: '9px' }}
                                              >
                                                {day.dayLabel}
                                              </div>
                                              {dayDetails.length === 0 ? (
                                                <div className="text-gray-300" style={{ fontSize: '9px' }}>
                                                  —
                                                </div>
                                              ) : (
                                                dayDetails.map((appt, ai) => (
                                                  <div
                                                    key={ai}
                                                    className="text-gray-600 truncate"
                                                    style={{ fontSize: '10px' }}
                                                    title={appt.subject || undefined}
                                                  >
                                                    {appt.time}{' '}
                                                    <span className="text-purple-500">
                                                      ({shortReason(appt.reason)})
                                                    </span>
                                                  </div>
                                                ))
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  );
                })()}
                {appointmentsData?.lastUpdatedAt && (
                  <div className="px-4 py-2 text-xs text-gray-500 bg-gray-50 border-t border-gray-200 italic">
                    Updated from VinSolutions hourly — last updated:{' '}
                    {formatLastUpdated(appointmentsData.lastUpdatedAt)}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
